'use client';

import { type ReactElement, useState } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';
import { Text } from '@ironhack/design-system2/components/text';
import { Flex } from '@ironhack/design-system2/components/flex';

import { FormThankYou } from '@/app/components/client/form-thank-you';
import { ModuleWrapper } from '@/app/components/server/module-wrapper';
import { type DatoButtonBlock } from '@/lib/datocms';
import { type ValidationMessageSet } from '@/lib/utils/form-validations';
import { type NameValidationMessages } from '@/lib/utils/form-validations/name-schema';
import { Form as FinancingFormComponent } from '../../components/form';

export type Form = {
  buttonText: string;
  chooseBetween: string;
  completeFields: string;
  emailLabel: string;
  emailPlaceholder: string;
  emailValidationMessages: ValidationMessageSet;
  errorMessage: string;
  firstNameLabel: string;
  firstNamePlaceholder: string;
  firstNameValidationMessages: NameValidationMessages;
  lastNameLabel: string;
  lastNamePlaceholder: string;
  lastNameValidationMessages: NameValidationMessages;
  legalText: StructuredTextGraphQlResponse;
  newsletterLabel: string;
  phoneInvalidMessage: string;
  phoneLabel: string;
  phonePlaceholder: string;
  phoneRequiredMessage: string;
  subtitle: string;
  thankYouText: string;
  thankYouTitle: string;
  title: string;
  withPhone?: boolean;
};

type Props = {
  data: { financingForm: Form; thankYouButton: [DatoButtonBlock] };
};

export const Form = (props: Props): ReactElement => {
  const {
    data: { financingForm, thankYouButton },
  } = props;
  const { title, subtitle, thankYouText, thankYouTitle } = financingForm;
  const [showThankYou, setShowThankYou] = useState(false);

  const handleSuccess = () => {
    setShowThankYou(true);
  };

  return (
    <ModuleWrapper
      innerProps={{ direction: 'column', alignItems: 'center' }}
      moduleFormat={{
        background: 'light',
        spacingBottom: '40',
        spacingTop: '40',
      }}
      outerProps={{}}
    >
      {!showThankYou && (
        <Flex
          bg={['background.light', null, 'background.white']}
          borderRadius="16px"
          direction="column"
          p={[0, null, 5]}
          w={['full', null, '680px']}
        >
          <Text as="h1" textStyle={['3xl', null, '5xl']}>
            {title}
          </Text>
          <Text mt={4} textStyle="l">
            {subtitle}
          </Text>
          <FinancingFormComponent
            financingForm={financingForm}
            onSuccess={handleSuccess}
          />
        </Flex>
      )}
      {showThankYou && (
        <FormThankYou
          bg={[null, null, 'background.white']}
          borderRadius={[null, null, '2xl']}
          button={thankYouButton}
          id="financingSuccess"
          p={[null, null, 5]}
          text={thankYouText}
          title={thankYouTitle}
          w={['full', null, '400px']}
        />
      )}
    </ModuleWrapper>
  );
};
